export const CONTACT_ATTACHMENT_MAX_COUNT = 5;
export const CONTACT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export const CONTACT_ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

const ALLOWED_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']);

export interface ContactAttachmentPayload {
  filename: string;
  contentType: string;
  data: string;
}

export interface ContactAttachmentPreview {
  id: string;
  file: File;
  previewUrl: string;
}

export function isAllowedContactAttachment(file: File): boolean {
  return ALLOWED_MIME_TYPES.has(file.type);
}

export function readContactAttachment(file: File): Promise<ContactAttachmentPayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const base64 = result.split(',')[1];
      if (!base64) {
        reject(new Error('Could not read file'));
        return;
      }
      resolve({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        data: base64,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export async function buildContactAttachments(files: File[]): Promise<ContactAttachmentPayload[]> {
  return Promise.all(files.map(readContactAttachment));
}

export function validateContactAttachmentSelection(
  currentCount: number,
  incomingFiles: File[],
): string | null {
  if (currentCount + incomingFiles.length > CONTACT_ATTACHMENT_MAX_COUNT) {
    return `You can attach up to ${CONTACT_ATTACHMENT_MAX_COUNT} images.`;
  }

  for (const file of incomingFiles) {
    if (!isAllowedContactAttachment(file)) {
      return 'Only PNG, JPG, GIF and WEBP images are allowed.';
    }
    if (file.size > CONTACT_ATTACHMENT_MAX_BYTES) {
      return 'Each image must be 5 MB or smaller.';
    }
  }

  return null;
}
